import {
  formatDurationMin,
  formatIstLabel12h,
  scheduleFieldsFromInfo,
  shouldShowKitchenTiming,
  useTaskFinishCountdown,
  useWorkTimingStatus,
} from "@/lib/productionScheduleUi";

function TaskWorkPill({ workMin }) {
  const label = formatDurationMin(workMin);
  if (!label) return null;
  return <span className="kitchen-task-work-pill">{label} work</span>;
}

function TaskTotalCakePill({ totalMin }) {
  const label = formatDurationMin(totalMin);
  if (!label) return null;
  return <span className="kitchen-task-total-pill">{label} total</span>;
}

function TaskAssigneePill({ name }) {
  if (!name) return null;
  return <span className="kitchen-task-assignee-pill">{name}</span>;
}

function TaskTimingStatusPill({ timing }) {
  if (!timing) return null;
  return (
    <span className={`kitchen-task-timing-pill kitchen-task-timing-pill--${timing.status}`}>
      {timing.label}
    </span>
  );
}

function TaskMetaPills({ assignedStaff, totalCakeMin, workMin, phasePills, timing }) {
  const hasMeta = assignedStaff || totalCakeMin || workMin || phasePills?.length || timing;
  if (!hasMeta) return null;
  return (
    <div className="kitchen-task-work-pills">
      <TaskAssigneePill name={assignedStaff} />
      <TaskTimingStatusPill timing={timing} />
      <TaskTotalCakePill totalMin={totalCakeMin} />
      {phasePills?.length ? (
        phasePills.map((pill) => {
          const dur = formatDurationMin(pill.duration_min);
          if (!dur) return null;
          return (
            <span key={pill.key || pill.label} className="kitchen-task-work-pill">
              {pill.label} {dur}
            </span>
          );
        })
      ) : (
        <TaskWorkPill workMin={workMin} />
      )}
    </div>
  );
}

export default function KitchenTaskScheduleBar({ info, done = false }) {
  const showTiming = shouldShowKitchenTiming({ ...info, done, is_done: info?.is_done });
  const fields = scheduleFieldsFromInfo(info || {});
  const shiftState = info?.shift_state || null;
  const clockOrder = {
    stage_start_at: fields.stage_start_at,
    stage_end_at: fields.stage_end_at,
    stage_start_label_ist: fields.stage_start_label_ist,
    stage_end_label_ist: fields.stage_end_label_ist,
    stage_deadline_at: fields.stage_deadline_at,
    stage_work_min: fields.stage_work_min,
    stage_duration_min: fields.stage_duration_min,
    shift_clock_mode: info?.shift_clock_mode,
    shift_clock_label: info?.shift_clock_label,
  };
  const { label, phase, mode } = useTaskFinishCountdown(clockOrder, done || !showTiming, shiftState);
  const timing = useWorkTimingStatus(clockOrder, done || !showTiming, 1000, shiftState);
  // Color tier follows the slot-truth status (behind = amber/recoverable, late = red)
  // so we never paint a still-safe cake red. Times shown are untouched.
  const tone =
    timing && (timing.status === "behind" || timing.status === "late")
      ? timing.status
      : phase || "active";

  const startLabel = formatIstLabel12h(fields.stage_start_label_ist);
  const endLabel = formatIstLabel12h(fields.stage_end_label_ist);
  const pills = fields.task_pills || [];
  const workMin = fields.stage_work_min;
  const totalCakeMin = fields.total_cake_min;
  const assignedStaff = fields.assigned_staff;

  if (!showTiming || (!label && !startLabel && !endLabel && !pills.length && !workMin && !totalCakeMin && !assignedStaff)) return null;

  const workWindow = startLabel && endLabel ? `${startLabel} – ${endLabel}` : (startLabel || endLabel);
  const isUpcoming = mode === "start" || mode === "closed";
  const isClosed = mode === "closed";
  const subLine = workWindow || null;

  return (
    <div className={`kitchen-task-big-timer kitchen-task-big-timer--${tone}`}>
      {label ? (
        <div className={`kitchen-task-big-timer-value ${isUpcoming ? "kitchen-task-big-timer-value--start" : ""}`}>
          {isUpcoming ? (
            <>
              <span className="kitchen-task-big-timer-hint">{isClosed ? "Scheduled" : "Start at"}</span>
              <span>{label}</span>
            </>
          ) : (
            label
          )}
        </div>
      ) : null}
      {subLine ? <div className="kitchen-task-big-timer-sub">{subLine}</div> : null}
      <TaskMetaPills
        assignedStaff={assignedStaff}
        totalCakeMin={totalCakeMin}
        workMin={workMin}
        phasePills={pills.length ? pills : null}
        timing={timing}
      />
    </div>
  );
}

export const KITCHEN_TASK_SCHEDULE_BAR_CSS = `
  .kitchen-task-big-timer {
    width: 100%;
    box-sizing: border-box;
    margin-bottom: 12px;
    padding: 16px 14px;
    border-radius: 16px;
    text-align: center;
    border: 2px solid rgba(180, 83, 9, 0.45);
    background: linear-gradient(180deg, #FFFBEB 0%, #FFFFFF 100%);
  }
  .kitchen-task-big-timer--upcoming,
  .kitchen-task-big-timer--active {
    border-color: rgba(180, 83, 9, 0.45);
    background: linear-gradient(180deg, #FFFBEB 0%, #FFFFFF 100%);
  }
  .kitchen-task-big-timer--upcoming {
    border-color: rgba(21, 128, 61, 0.45);
    background: linear-gradient(180deg, #F0FDF4 0%, #FFFFFF 100%);
  }
  .kitchen-task-big-timer--closed {
    border-color: rgba(100, 116, 139, 0.4);
    background: linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%);
  }
  .kitchen-task-big-timer--behind {
    border-color: rgba(217, 119, 6, 0.55);
    background: linear-gradient(180deg, #FFF7ED 0%, #FFFFFF 100%);
  }
  .kitchen-task-big-timer--late {
    border-color: rgba(185, 28, 28, 0.5);
    background: linear-gradient(180deg, #FEF2F2 0%, #FFFFFF 100%);
  }
  .kitchen-task-big-timer-value {
    font-size: 36px;
    font-weight: 900;
    font-variant-numeric: tabular-nums;
    line-height: 1.05;
    letter-spacing: -0.02em;
  }
  .kitchen-task-big-timer-value--start {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    color: #15803D;
  }
  .kitchen-task-big-timer-hint {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(21, 128, 61, 0.75);
  }
  .kitchen-task-big-timer--active .kitchen-task-big-timer-value,
  .kitchen-task-big-timer--upcoming .kitchen-task-big-timer-value:not(.kitchen-task-big-timer-value--start) {
    color: #B45309;
  }
  .kitchen-task-big-timer--behind .kitchen-task-big-timer-value {
    color: #C2410C;
  }
  .kitchen-task-big-timer--late .kitchen-task-big-timer-value {
    color: #B91C1C;
  }
  .kitchen-task-big-timer-sub {
    margin-top: 6px;
    font-size: 12px;
    font-weight: 700;
    color: rgba(45, 45, 45, 0.55);
    font-variant-numeric: tabular-nums;
  }
  .kitchen-task-work-pills {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
    margin-top: 10px;
  }
  .kitchen-task-work-pill {
    font-size: 10px;
    font-weight: 800;
    padding: 4px 10px;
    border-radius: 999px;
    background: #FCE7F3;
    color: #EE2677;
    border: 1px solid rgba(238, 38, 119, 0.28);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  .kitchen-task-total-pill {
    font-size: 10px;
    font-weight: 800;
    padding: 4px 10px;
    border-radius: 999px;
    background: #E0F2FE;
    color: #0369A1;
    border: 1px solid rgba(3, 105, 161, 0.22);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  .kitchen-task-assignee-pill {
    font-size: 10px;
    font-weight: 800;
    padding: 4px 10px;
    border-radius: 999px;
    background: #CCFBF1;
    color: #0F766E;
    border: 1px solid rgba(15, 118, 110, 0.25);
    letter-spacing: 0.02em;
  }
  .kitchen-task-timing-pill {
    font-size: 10px;
    font-weight: 800;
    padding: 4px 10px;
    border-radius: 999px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  .kitchen-task-timing-pill--ontime {
    background: #DCFCE7;
    color: #166534;
  }
  .kitchen-task-timing-pill--hurry {
    background: #FFEDD5;
    color: #9A3412;
  }
  .kitchen-task-timing-pill--behind {
    background: #FFEDD5;
    color: #C2410C;
    border: 1px solid rgba(217, 119, 6, 0.45);
  }
  .kitchen-task-timing-pill--late {
    background: #FEE2E2;
    color: #B91C1C;
  }
`;

export const KITCHEN_SCHEDULE_PHASE_CSS = "";
